import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { APIError } from "@/adapters/api";
import { AccountUnavailable } from "@/components/account-unavailable";
import { AuthScreenLayout } from "@/features/auth/components/auth-screen-layout";
import { VerificationShell } from "@/features/auth/components/verification-shell";
import { VerifyEmailNotice } from "@/features/auth/components/verify-email-notice";
import { useResendVerification } from "@/features/auth/hooks/use-resend-verification";
import { useSignup } from "@/features/auth/hooks/use-signup";
import { AccountStep } from "@/features/onboarding/components/account-step";
import { ModeStep } from "@/features/onboarding/components/mode-step";
import { OnboardingShell } from "@/features/onboarding/components/onboarding-shell";
import { ProfileStep } from "@/features/onboarding/components/profile-step";
import { SingleProjectStep } from "@/features/onboarding/components/single-project-step";
import { SiteStep } from "@/features/onboarding/components/site-step";
import { SummaryStep } from "@/features/onboarding/components/summary-step";
import { TeamStep } from "@/features/onboarding/components/team-step";
import { profileNeedsCompanyName } from "@/features/onboarding/data";
import { useCompleteOnboarding } from "@/features/onboarding/hooks/use-complete-onboarding";
import { useOnboardingFlow } from "@/features/onboarding/hooks/use-onboarding-flow";
import type { AccountFormValues } from "@/features/onboarding/schemas/onboarding-schema";
import { buildOnboardingRequest } from "@/features/onboarding/utils/build-onboarding-request";
import {
	earliestViolationStep,
	scopeAccountViolations,
	scopeStructureViolations,
	scopeTeamViolations,
} from "@/features/onboarding/utils/violations";
import { WaitlistInviteError } from "@/features/waitlist/components/waitlist-invite-error";
import { WaitlistStep } from "@/features/waitlist/components/waitlist-step";
import { useWaitlistInvitePreview } from "@/features/waitlist/hooks/use-waitlist-invite-preview";
import { useAuth } from "@/store/use-auth";

const RESEND_COOLDOWN_MS = 30 * 1000;

export function OnboardingPage() {
	const navigate = useNavigate();
	const { token } = useParams();
	const invitePreview = useWaitlistInvitePreview(token);

	const [initialSession] = useState(() => useAuth.getState().session);
	const flow = useOnboardingFlow(
		initialSession
			? { startStep: "profile", account: initialSession.user }
			: undefined,
	);

	const signup = useSignup();
	const resendVerification = useResendVerification();
	const completeOnboarding = useCompleteOnboarding();
	const [accountUnavailable, setAccountUnavailable] = useState(false);
	const [pending, setPending] = useState<{
		values: AccountFormValues;
		renewAfter: string;
	} | null>(null);
	const [accountViolations, setAccountViolations] = useState<
		Record<string, string>
	>({});
	const [signupError, setSignupError] = useState<string>();
	const [violations, setViolations] = useState<Record<string, string>>({});
	const [submitError, setSubmitError] = useState<string>();

	const askCompanyName = profileNeedsCompanyName(flow.data.profile?.type);

	function submitSignup(values: AccountFormValues) {
		if (!token) {
			return;
		}
		setAccountViolations({});
		setSignupError(undefined);
		signup.mutate(
			{
				name: values.name,
				email: values.email,
				password: values.password,
				invite_token: token,
			},
			{
				onSuccess: () => {
					flow.setAccount({ name: values.name, email: values.email });
					setPending({
						values,
						renewAfter: new Date(
							Date.now() + RESEND_COOLDOWN_MS,
						).toISOString(),
					});
				},
				onError: (err) => {
					if (err instanceof APIError && err.code === "validation") {
						setAccountViolations(err.violations);
						return;
					}
					setSignupError(
						"Não foi possível concluir o cadastro. Tente novamente.",
					);
				},
			},
		);
	}

	function resendPendingVerification(email: string) {
		resendVerification.mutate(email, {
			onSuccess: () =>
				setPending((current) =>
					current
						? {
								...current,
								renewAfter: new Date(
									Date.now() + RESEND_COOLDOWN_MS,
								).toISOString(),
							}
						: current,
				),
		});
	}

	function finishOnboarding() {
		setViolations({});
		setSubmitError(undefined);
		completeOnboarding.mutate(buildOnboardingRequest(flow.data), {
			onSuccess: () => navigate("/"),
			onError: (err) => {
				if (err instanceof APIError) {
					if (
						err.reason === "onboarding-already-completed" ||
						err.code === "onboarding-already-completed"
					) {
						setAccountUnavailable(true);
						return;
					}
					if (err.code === "validation") {
						setViolations(err.violations);
						const step = earliestViolationStep(err.violations);
						if (step) flow.goTo(step);
						return;
					}
				}
				setSubmitError(
					"Não foi possível concluir o cadastro. Verifique sua conexão e tente novamente.",
				);
			},
		});
	}

	if (accountUnavailable) {
		return (
			<AccountUnavailable
				title="Configuração de conta indisponível"
				description="Este usuário já concluiu o cadastro, mas nenhuma conta está disponível. Saia e entre novamente para continuar."
			/>
		);
	}

	if (flow.step === "account" && pending) {
		return (
			<VerificationShell>
				<VerifyEmailNotice
					email={pending.values.email}
					renewAfter={pending.renewAfter}
					resending={resendVerification.isPending}
					onResend={() => resendPendingVerification(pending.values.email)}
					onChangeEmail={() => {
						setPending(null);
						signup.reset();
						resendVerification.reset();
					}}
				/>
			</VerificationShell>
		);
	}

	if (flow.step === "account" && !token) {
		return (
			<AuthScreenLayout>
				<WaitlistStep />
			</AuthScreenLayout>
		);
	}

	if (flow.step === "account") {
		if (invitePreview.isError) {
			const reason =
				invitePreview.error instanceof APIError
					? invitePreview.error.reason
					: "";
			return (
				<VerificationShell>
					<WaitlistInviteError reason={reason} />
				</VerificationShell>
			);
		}

		if (!invitePreview.data) {
			return (
				<div className="route-loading">
					<Loader2 />
				</div>
			);
		}

		return (
			<AuthScreenLayout>
				<AccountStep
					defaultValues={{
						...(flow.data.account ?? {}),
						email: invitePreview.data.email,
					}}
					emailReadOnly
					hideGoogle
					loading={signup.isPending}
					error={signupError}
					violations={scopeAccountViolations(accountViolations)}
					onSubmit={submitSignup}
				/>
			</AuthScreenLayout>
		);
	}

	return (
		<OnboardingShell
			currentStep={flow.stepIndex - 1}
			onExit={() => navigate("/login")}
		>
			{flow.step === "profile" && (
				<ProfileStep
					initialProfile={flow.data.profile}
					onBack={flow.back}
					onContinue={(type) => {
						flow.setProfile({
							type,
							companyName: profileNeedsCompanyName(type)
								? (flow.data.profile?.companyName ?? null)
								: null,
						});
						flow.next();
					}}
				/>
			)}

			{flow.step === "mode" && (
				<ModeStep
					value={flow.data.mode}
					onChange={flow.setMode}
					onBack={flow.back}
					onContinue={flow.next}
				/>
			)}

			{flow.step === "structure" &&
				(flow.data.mode === "single-project" ? (
					<SingleProjectStep
						initialProject={flow.data.singleProject}
						askCompanyName={askCompanyName}
						initialCompanyName={flow.data.profile?.companyName ?? ""}
						violations={scopeStructureViolations(violations)}
						onBack={flow.back}
						onContinue={(project, companyName) => {
							flow.setSingleProject(project);
							if (companyName !== null && flow.data.profile) {
								flow.setProfile({
									type: flow.data.profile.type,
									companyName,
								});
							}
							flow.next();
						}}
					/>
				) : (
					<SiteStep
						initialSite={flow.data.site}
						askCompanyName={askCompanyName}
						initialCompanyName={flow.data.profile?.companyName ?? ""}
						violations={scopeStructureViolations(violations)}
						onBack={flow.back}
						onContinue={(site, companyName) => {
							flow.setSite(site);
							if (companyName !== null && flow.data.profile) {
								flow.setProfile({
									type: flow.data.profile.type,
									companyName,
								});
							}
							flow.next();
						}}
					/>
				))}

			{flow.step === "team" && (
				<TeamStep
					invites={flow.data.invites}
					violations={scopeTeamViolations(violations, flow.data.invites)}
					onAddInvite={flow.addInvite}
					onRemoveInvite={flow.removeInvite}
					onBack={flow.back}
					onContinue={flow.next}
				/>
			)}

			{flow.step === "summary" && (
				<SummaryStep
					data={flow.data}
					submitting={completeOnboarding.isPending}
					error={submitError}
					onBack={flow.back}
					onFinish={finishOnboarding}
				/>
			)}
		</OnboardingShell>
	);
}
